import { defineStore } from 'pinia';
import type { dynamicObj } from 'src/pages/assets';
import { masterDataApiService } from 'src/services';
import { ApiExceptions } from 'src/exceptions/index';
import {
  isObjectEmtpy,
  isUndefinedOrNull,
  isEmptyString,
} from 'src/utils/general';
import {
  makeOrderByJsonForApi,
  convertLabel,
  getSavedOrderByColumns,
} from 'src/utils/ms_utlis';
import { useDocumentStore } from 'src/store/main';
import { notify } from 'src/components/notifications/notify';

export const useLookupFunctionStore = defineStore('lookup_function_store', {
  state: () => ({
    docstore: useDocumentStore(),
    lookup_loading_fields: {} as dynamicObj,
    table_lookup_loading_fields: {} as dynamicObj,
    orderByMap: new Map() as Map<string, string>,
    trigger_lookup_flow: '' as string,
  }),
  actions: {
    async searchByField(
      filters: dynamicObj,
      master_lookup_type: string,
      form_data: dynamicObj = {},
    ) {
      try {
        if (this.validateSearchFilters(filters) === false) {
          return null;
        }

        //Prepare querystring for search
        const data = this.prepareFiltersForApiCall(filters, master_lookup_type);
        if (
          isUndefinedOrNull(data['filter_data_json']) ||
          isEmptyString(data['filter_data_json'])
        ) {
          return null;
        }

        const response = await this.fetchFromServer(data, form_data);
        return response;
      } catch (error) {
        logError(error);
      }
    },

    async fetchFromServer(search_data: dynamicObj, form_data: dynamicObj = {}) {
      try {
        const data = {} as dynamicObj;
        data['data_type'] = search_data['data_type'];
        data['skip'] = 0;
        data['limit'] = 10;

        this.orderByMap = getSavedOrderByColumns(data['data_type']);
        const order_by = makeOrderByJsonForApi(this.orderByMap);
        if (order_by.length > 0) {
          data['sort_order_json'] = JSON.stringify(order_by);
        }

        const sub_org_id = this.docstore.getDocSubOrgId();
        if (!isUndefinedOrNull(sub_org_id) && sub_org_id != '') {
          data['sub_org_id'] = sub_org_id;
        }

        let response = {} as dynamicObj;
        response = await masterDataApiService.getData_V2(
          data,
          search_data['filter_data_json'],
          form_data,
        );

        if (response.success == false) {
          throw response;
        }

        if (response.data && response.data.length > 0) {
          return response;
        }

        if (
          !response.data ||
          !Array.isArray(response.data) ||
          response.data.length == 0
        ) {
          logInfo('No Data From server');
          return null;
        }

        return null;
      } catch (err: any) {
        const msg = ApiExceptions.getErrorMessage(err);
        // notify.error(msg);
        logError(err);
      }
    },

    validateSearchFilters(filters: dynamicObj = {}) {
      let is_valid = true;
      const advance_filters: dynamicObj = JSON.parse(JSON.stringify(filters));

      for (const [index, column] of Object.entries(advance_filters)) {
        column.validate = false;
        column.validate_field_name = false;
        // const is_field_selected = column.hasOwnProperty('field') && column.field;
        // const is_text_entered = column.search_text && column.search_text.length > 0;

        if (!column.field && column.search_text) {
          column.validate_field_name = true;
          is_valid = false;
        }

        if (!column.search_text && column.field) {
          column.validate = true;
          is_valid = false;
        }
      }
      return is_valid;
    },

    prepareFiltersForApiCall(
      advance_filters: dynamicObj,
      master_lookup_type: string,
    ) {
      const data = {} as dynamicObj;
      data['data_type'] = master_lookup_type;
      data['filter_data_json'] = '';

      const filters = this.makeSearchJsonFromFilters(advance_filters);

      if (filters.length > 0) {
        data['filter_data_json'] = JSON.stringify({ filters });
      }

      return data;
    },

    makeSearchJsonFromFilters(advance_filters: dynamicObj) {
      const filters = [] as dynamicObj[];

      advance_filters = JSON.parse(JSON.stringify(advance_filters));
      for (const [index, column] of Object.entries(advance_filters)) {
        if (column.search_text) {
          filters.push({
            field: column.field.field,
            operator: column.operator,
            value: column.search_text,
          });
        }
      }
      return filters;
    },

    interpolate(field_name: string, value: string) {
      const re = /%name/gi;
      const str = convertLabel(
        this.lookup_loading_fields[field_name].lookup_found_name,
      );
      const result = value.replace(re, str);
      return result;
    },
  },
});
